// create an empty hello world stenciljs component
import { Component, Prop, h, Event, Listen, EventEmitter, State } from '@stencil/core';
import { Option, FacetUpdateEvent, FacetChangeEvent, FacetItem, getData, logDebug } from '@hpfc/fc-frontend-core';

/**
 * A list of checkboxes within a fieldset, with an optional label.
 *
 * The checkbox data can be passed in as a `Prop`, or using an `fccdComponentEvent`.
 */
@Component({
  tag: 'fccd-checkbox-list',
})
export class CheckboxList {
  /**
   * Unique identifier for the element.
   *
   * Also used to identify the element in the fccdFacetChangeEvent event model.
   */
  @Prop() componentName!: string;
  /**
   * If true, the optional label for the checkboxes will be hidden.
   */
  @Prop() hideInputOptionalLabel: boolean = true;
  /**
   * Optional message for the fieldset.
   */
  @Prop() label?: string;
  /**
   * Size of the fieldset message.
   */
  @Prop() labelSize: 'lg' | 'md' | 'sm' | 'xs' = 'xs';
  /**
   * Type of the fieldset message.
   */
  @Prop() labelType: 'body' | 'heading' = 'heading';
  /**
   * The initial value for the dropdown options. This Prop can be overwritten by data coming from an fccdComponentEvent.
   *
   * The options can be passed in as a JSON string, or an array of Option objects.
   */
  @Prop() options: string | Array<Option>;
  /**
   * Indicates whether the checkbox fieldset is required.  Set to 'false' by default.
   */
  @Prop() required?: boolean = false;

  /**
   * The amount of vertical space to add above the checklist
   */
  @Prop() verticalSpacing: 'lg' | 'md' | 'sm' | 'xs' | 0 = 0;

  /**
   * Dropdown options to display.
   */
  @State() data: Array<Option>;

  /**
   * Hide the checkbox
   */
  @State() hidden: boolean = false;

  /**
   * Dispatches this event when a selection is made.
   */
  @Event() fccdFacetChangeEvent: EventEmitter<FacetChangeEvent>;

  /**
   * Sets the initial value for the dropdown options.
   */
  componentWillLoad() {
    if (!this.data && this.options) {
      this.data = getData(this.options);
    }
  }

  /**
   * Handles the change event for the checkbox.
   */
  @Listen('hp5ValidationChange')
  selectHandler(event: CustomEvent) {
    if (!this.data) {
      return;
    }
    // Get data from the event
    const changedState = event?.detail?.originalEvent?.target?.checked;
    if (changedState === undefined) {
      logDebug('Could not determine whether the checkbox was checked or unchecked.');
      return;
    }
    const changedValue = event.detail.value;

    // Update the data state
    const optionToUpdate = this.data.find(option => option.value === changedValue);
    if (optionToUpdate) {
      optionToUpdate.isSelected = changedState;
    }

    // Get all selected values
    const values = this.data.filter(option => option.isSelected).map(option => option.value);

    // Dispatch the event
    const facetItem = new FacetItem(this.componentName, values, true);
    this.fccdFacetChangeEvent.emit(new FacetChangeEvent(event, facetItem));
  }

  /**
   * Updates the dropdown if a matching fccdComponentEvent is received.
   *
   * The event must have a facetOptionMap with a key that matches the elementId.
   */
  @Listen('fccdComponentEvent', { target: 'document' })
  updateHandler(event: CustomEvent<FacetUpdateEvent>) {
    const hasNewData = event?.detail?.facetOptionMap?.has(this.componentName);
    if (!hasNewData) {
      return;
    }
    this.data = event.detail.facetOptionMap.get(this.componentName);

    const facetItem = event?.detail?.facetMap?.get(this.componentName);
    if (facetItem) {
      // we want to hide the checkbox list if the facet is marked as disabled
      this.hidden = facetItem.disabled || false;
    }
  }

  /**
   * Indicates whether the checkbox data is ready to be displayed.
   * @returns true if the checkboxes should be visible, false otherwise.
   */
  isVisible = (): boolean => Boolean(this.data && this.data.length > 0 && !this.hidden);

  private callback: () => void;

  componentDidRender() {
    if (this.callback) {
      this.callback();
      this.callback = null;
    }
  }

  render() {
    if (!this.isVisible()) {
      return null;
    }
    return (
      <div>
        <hp5-spacing-vertical xs={this.verticalSpacing} />
        <hp5-fieldset
          hide-input-optional-label={this.hideInputOptionalLabel}
          legend-message={this.label}
          legend-size={this.labelSize}
          legend-type={this.labelType}
          required={this.required}
        >
          {this.data.map(option => (
            <hp5-input-checkbox
              checked={option.isSelected}
              element-id={this.componentName + option.value}
              hide-input-optional-label={this.hideInputOptionalLabel}
              label={option.label}
              name={this.componentName + option.value}
              required={false}
              value={option.value}
            />
          ))}
        </hp5-fieldset>
      </div>
    );
  }
}
